import React, { useState, useEffect, Fragment } from "react";
import { collection, getDocs, deleteDoc, doc, addDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/firebase-config";
import { useDispatch } from "react-redux";
import { setPageTitle } from "../../store/themeConfigSlice";
import { Dialog, Transition } from "@headlessui/react";

const PatientList = () => {
    const [patients, setPatients] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [formData, setFormData] = useState({ name: "", email: "" });
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setPageTitle("Patient List"));
        fetchPatients();
    }, [dispatch]);

    const fetchPatients = async () => {
        const querySnapshot = await getDocs(collection(db, "patients"));
        const data = querySnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        setPatients(data);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this patient?")) {
            try {
                await deleteDoc(doc(db, "patients", id));
                setPatients(patients.filter((p) => p.id !== id));
            } catch (error) {
                console.error("Error deleting patient:", error);
            }
        }
    };

    const openModal = (patient = null) => {
        setSelectedPatient(patient);
        setFormData(patient ? { name: patient.name, email: patient.email } : { name: "", email: "" });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedPatient(null);
    };

    const handleSave = async () => {
        if (!formData.name || !formData.email) {
            alert("Please enter name and email");
            return;
        }

        try {
            if (selectedPatient) {
                await updateDoc(doc(db, "patients", selectedPatient.id), formData);
            } else {
                await addDoc(collection(db, "patients"), formData);
            }
            fetchPatients();
            closeModal();
        } catch (error) {
            console.error("Error saving patient:", error);
        }
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-2">
                <h1 className="text-2xl font-bold mb-4">Patient List</h1>
                <button onClick={() => openModal()} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition">
                    Add New Patient
                </button>
            </div>

            <table className="w-full border-collapse border border-gray-300 dark:border-gray-700">
                <thead>
                <tr className="bg-gray-200 dark:bg-gray-800">
                    <th className="border p-2">No.</th>
                    <th className="border p-2">Name</th>
                    <th className="border p-2">Email</th>
                    <th className="border p-2">Actions</th>
                </tr>
                </thead>
                <tbody>
                {patients.map((patient, index) => (
                    <tr key={patient.id} className="hover:bg-gray-100 dark:hover:bg-gray-700">
                        <td className="border p-2 text-center">{index + 1}</td>
                        <td className="border p-2">{patient.name}</td>
                        <td className="border p-2">{patient.email}</td>
                        <td className="border p-2 text-center">
                            <button
                                onClick={() => openModal(patient)}
                                className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 transition mr-2"
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => handleDelete(patient.id)}
                                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition"
                            >
                                Delete
                            </button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            {/* Modal */}
            <Transition appear show={isModalOpen} as={Fragment}>
                <Dialog as="div" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClose={closeModal}>
                    <Dialog.Panel className="panel my-8 w-full max-w-md bg-white dark:bg-gray-900 rounded-lg shadow-lg p-4">
                        <Dialog.Title className="text-lg font-bold text-gray-800 dark:text-white">
                            {selectedPatient ? "Edit Patient" : "Add New Patient"}
                        </Dialog.Title>

                        <div className="mt-4 space-y-4">
                            <div className="flex flex-col">
                                <label className="text-gray-700 dark:text-gray-300">Name</label>
                                <input
                                    type="text"
                                    className="border p-2 rounded focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-white dark:border-gray-700 dark:focus:ring-blue-500"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div className="flex flex-col">
                                <label className="text-gray-700 dark:text-gray-300">Email</label>
                                <input
                                    type="email"
                                    className="border p-2 rounded focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-white dark:border-gray-700 dark:focus:ring-blue-500"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end space-x-3">
                            <button className="bg-gray-400 dark:bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-500 dark:hover:bg-gray-600 transition" onClick={closeModal}>
                                Cancel
                            </button>
                            <button className="bg-blue-600 dark:bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition" onClick={handleSave}>
                                {selectedPatient ? "Update" : "Save"}
                            </button>
                        </div>
                    </Dialog.Panel>
                </Dialog>
            </Transition>
        </div>
    );
};

export default PatientList;
